import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import {
    Team,
    loadSettings,
    saveSettings,
    clampSettings,
    getWinner
} from './gameUtils';
import ScoreSide from './components/ScoreSide';
import SettingsModal from './components/SettingsModal';
import ScoreAnimationToast from './components/ScoreAnimationToast';
import { publishScoreChanged } from './scoreEvents';

function App() {
    const [settings, setSettings] = useState(loadSettings);
    const [redScore, setRedScore] = useState(0);
    const [blueScore, setBlueScore] = useState(0);
    const [showSettings, setShowSettings] = useState(false);
    const previousScoreRef = useRef({ redScore, blueScore });

    useEffect(() => {
        saveSettings(settings);
    }, [settings]);

    useEffect(() => {
        const previousScore = previousScoreRef.current;
        if (previousScore.redScore === redScore && previousScore.blueScore === blueScore) {
            return;
        }

        publishScoreChanged({ redScore, blueScore });
        previousScoreRef.current = { redScore, blueScore };
    }, [redScore, blueScore]);

    const winner = getWinner(redScore, blueScore, settings);
    const isGameOver = winner !== null;

    function increment(team) {
        if (isGameOver) return;
        if (team === Team.RED) {
            setRedScore((prev) => Math.min(prev + 1, settings.maxScore));
        } else {
            setBlueScore((prev) => Math.min(prev + 1, settings.maxScore));
        }
    }

    function decrement(team) {
        if (team === Team.RED) {
            setRedScore((prev) => Math.max(prev - 1, 0));
        } else {
            setBlueScore((prev) => Math.max(prev - 1, 0));
        }
    }

    function clearScores() {
        setRedScore(0);
        setBlueScore(0);
    }

    function handleGameAction() {
        if (isGameOver) {
            clearScores();
            return;
        }

        if (window.confirm('Reset the score?')) {
            clearScores();
        }
    }

    function handleSettingsChange(newSettings) {
        setSettings(clampSettings(newSettings));
    }

    const winnerText = winner
        ? `${winner === Team.RED ? settings.redTeamName : settings.blueTeamName} wins!`
        : null;

    return (
        <div className="app">
            <header className="header">
                <button
                    className={`header-button ${isGameOver ? 'new-set-button' : 'reset-button'}`}
                    onClick={handleGameAction}
                >
                    {isGameOver ? 'New Set' : 'Reset'}
                </button>
                <h1>Volley Score</h1>
                <button
                    className="header-button settings-button"
                    onClick={() => setShowSettings(true)}
                >
                    Settings
                </button>
            </header>
            <div className="game">
                <ScoreSide
                    teamName={settings.redTeamName}
                    score={redScore}
                    isWinner={winner === Team.RED}
                    onIncrement={() => increment(Team.RED)}
                    onDecrement={() => decrement(Team.RED)}
                    colorClass="red"
                />
                <ScoreSide
                    teamName={settings.blueTeamName}
                    score={blueScore}
                    isWinner={winner === Team.BLUE}
                    onIncrement={() => increment(Team.BLUE)}
                    onDecrement={() => decrement(Team.BLUE)}
                    colorClass="blue"
                />
            </div>
            {winnerText && (
                <div className="winner-overlay">
                    <div className="winner-message">
                        <h2>{winnerText}</h2>
                        <p>Game over. Start a new set when you're ready.</p>
                    </div>
                </div>
            )}
            <ScoreAnimationToast enabled={settings.showScoreAnimations} />
            {showSettings && (
                <SettingsModal
                    settings={settings}
                    onChange={handleSettingsChange}
                    onClose={() => setShowSettings(false)}
                />
            )}
        </div>
    );
}

export default App;
